import { OneTimeDataRequestBuilder } from '@radixdlt/radix-dapp-toolkit';
import { supabase } from '../supabase';

export const RADCOON_AUTH_SESSION_KEY = 'radcoon_secure_session';
export const USER_SESSION_KEY = 'radcoon_authenticated_user';

const getSessionStorage = () => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage;
};

const invokeAuthFunction = async (body, sessionToken = null) => {
  const { data, error } = await supabase.functions.invoke('radcoon-auth', {
    body,
    headers: sessionToken ? { 'x-radcoon-session': sessionToken } : undefined,
  });

  if (error) {
    return { error };
  }

  if (data?.success === false) {
    return { error: new Error(data.error || 'Authentication failed') };
  }

  return { data };
};

export const getStoredRadcoonSession = () => {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const rawSession = storage.getItem(RADCOON_AUTH_SESSION_KEY);
    if (!rawSession) return null;

    const session = JSON.parse(rawSession);
    if (!session?.sessionToken || !session?.user?.id || !session?.expiresAt) {
      clearStoredRadcoonSession();
      return null;
    }

    if (new Date(session.expiresAt).getTime() <= Date.now()) {
      clearStoredRadcoonSession();
      return null;
    }

    return session;
  } catch (error) {
    console.error('Error reading Radcoon session:', error);
    clearStoredRadcoonSession();
    return null;
  }
};

export const getRadcoonSessionToken = () => {
  return getStoredRadcoonSession()?.sessionToken || null;
};

export const clearStoredRadcoonSession = () => {
  const storage = getSessionStorage();
  if (!storage) return;

  storage.removeItem(RADCOON_AUTH_SESSION_KEY);
  storage.removeItem(USER_SESSION_KEY);
};

const storeRadcoonSession = (session) => {
  const storage = getSessionStorage();
  if (!storage) return;

  storage.setItem(RADCOON_AUTH_SESSION_KEY, JSON.stringify(session));
  storage.setItem(USER_SESSION_KEY, JSON.stringify(session.user));
};

const unwrapWalletResult = (result) => {
  if (!result) {
    throw new Error('Wallet did not return an ownership proof.');
  }

  if (typeof result.isErr === 'function' && result.isErr()) {
    throw result.error || new Error('Wallet proof request failed.');
  }

  if (typeof result.isOk === 'function' && result.isOk()) {
    return result.value;
  }

  return result;
};

const findAccountProof = (walletData, walletAddress) => {
  const proofs = [
    ...(Array.isArray(walletData?.proofs) ? walletData.proofs : []),
    ...(Array.isArray(walletData?.accounts?.proofs) ? walletData.accounts.proofs : []),
    ...(Array.isArray(walletData?.data?.proofs) ? walletData.data.proofs : []),
    ...(Array.isArray(walletData?.data?.accounts?.proofs) ? walletData.data.accounts.proofs : []),
  ];

  const accountProof = proofs.find((proof) => {
    const address = proof?.address || proof?.accountAddress;

    return (
      (proof?.type === 'account' || proof?.accountAddress) &&
      address?.toLowerCase() === walletAddress.toLowerCase()
    );
  });

  if (!accountProof) return null;
  if (accountProof.type === 'account' && accountProof.address) return accountProof;

  return {
    type: 'account',
    address: accountProof.accountAddress,
    challenge: accountProof.challenge || walletData?.challenge || walletData?.data?.challenge,
    proof: accountProof.proof,
  };
};

/**
 * Authenticate with a Radix wallet ownership proof verified by the radcoon-auth Edge Function.
 * @param {string} walletAddress - Selected Radix account address.
 * @param {Object} rdt - Radix Dapp Toolkit instance from useRadixConnect.
 * @returns {Promise<Object>} User/session data or error.
 */
export async function authenticateWithWallet(walletAddress, rdt) {
  if (!walletAddress) {
    return { error: new Error('No wallet address provided') };
  }

  if (!rdt?.walletApi) {
    return { error: new Error('Radix wallet proof API is not available yet') };
  }

  try {
    const challengeResult = await invokeAuthFunction({
      action: 'createChallenge',
      walletAddress,
    });

    if (challengeResult.error) {
      return { error: challengeResult.error };
    }

    const { challengeId, challenge } = challengeResult.data;
    if (!challengeId || !challenge) {
      return { error: new Error('Auth challenge response was incomplete') };
    }

    rdt.walletApi.provideChallengeGenerator(async () => challenge);

    const walletResult = await rdt.walletApi.sendOneTimeRequest(
      OneTimeDataRequestBuilder.proofOfOwnership().accounts([walletAddress])
    );
    const walletData = unwrapWalletResult(walletResult);
    const signedChallenge = findAccountProof(walletData, walletAddress);

    if (!signedChallenge) {
      return { error: new Error('Wallet did not prove ownership of the selected account') };
    }

    const verifyResult = await invokeAuthFunction({
      action: 'verifyChallenge',
      challengeId,
      walletAddress,
      signedChallenge,
    });

    if (verifyResult.error) {
      return { error: verifyResult.error };
    }

    const session = {
      user: verifyResult.data.user,
      sessionToken: verifyResult.data.sessionToken,
      expiresAt: verifyResult.data.expiresAt,
      walletAddress,
    };

    storeRadcoonSession(session);

    return {
      user: session.user,
      sessionToken: session.sessionToken,
      expiresAt: session.expiresAt,
      isNewUser: false,
    };
  } catch (error) {
    console.error('Unexpected error during secure authentication:', error);
    return { error };
  }
}

/**
 * Check local Radcoon session state. Backend authorization still happens per Edge Function call.
 * @param {string} userId - User ID to check.
 * @returns {Promise<Object>} Authentication state.
 */
export async function checkAuthentication(userId) {
  if (!userId) {
    return { authenticated: false, error: new Error('No user ID provided') };
  }

  const session = getStoredRadcoonSession();
  if (!session || session.user?.id !== userId) {
    return { authenticated: false, error: new Error('No valid Radcoon session') };
  }

  return {
    authenticated: true,
    user: session.user,
    sessionToken: session.sessionToken,
    expiresAt: session.expiresAt,
  };
}

export async function logoutRadcoonSession() {
  const sessionToken = getRadcoonSessionToken();

  if (sessionToken) {
    await invokeAuthFunction({ action: 'logout' }, sessionToken);
  }

  clearStoredRadcoonSession();
}
